/**
 * A saved session read back as its five steps — S1 Participants, S2 Items,
 * S3 Criteria, S4 Values, S5 Ranking (canon Doc 02 §2A.1) — for one item it
 * ranked. The item page shows it in a card so a person can see how the session
 * got from who was in the room to where this item landed.
 *
 * Every session has the five steps, whether or not it was set up in the Event
 * Designer: the people are on `session_participants`, the items on `item_ids`,
 * the criteria on `criteria_config`, the result on `session_results`. Most
 * sessions on prod (66 of 89 with results, 2026-10-16) carry no
 * `mode_config.v3Config`; for those this reads the ordinary columns. A designer
 * session adds how each step was settled (set by the facilitator, or decided
 * together with a tool) and the per-item values.
 *
 * Pure: no store, no fetch.
 */
import { isValuesFrom, valuesFromSentence } from '@/lib/valuesFrom';
import { resolveSessionScoringCriteria, type CriterionLabelSource, type SessionCriteriaCarrier } from '../../lib/sessionScoringCriteria';
import { normalizeSessionToolId, resolveSessionToolDescriptor, toolAsksForCriteria } from '../../lib/sessionToolCatalog';
import { STEP_META, STEP_ORDER, type StepConfig, type StepId } from './types';

/** The parts of a session row this reads. Structural, so a partial row will do. */
export interface SessionStepsSource extends SessionCriteriaCarrier {
  selectedToolId?: string | null;
  participantIds?: string[] | null;
  itemIds?: string[] | null;
  isolatedItems?: string[] | null;
  rankingOutput?: { rankedItemIds?: string[] | null } | null;
  runCompletedAt?: string | null;
}

export interface SessionStepsContext {
  /** The item the card is about. */
  itemId: string;
  /** Where that item landed in this session, and out of how many. */
  rank?: number | null;
  outOf?: number | null;
  /** A person's name from their user id. */
  nameOf?: (userId: string) => string | null | undefined;
  /** A criterion's label from its attribute key. */
  attributeFor?: (id: string) => CriterionLabelSource | null | undefined;
}

/** Filled = done, half = partly, open = not yet (the design system's fill rule). */
export type SessionStepState = 'done' | 'partial' | 'open';

export interface SessionStepLine {
  step: StepId;
  /** "S1 · Participants" */
  label: string;
  state: SessionStepState;
  value: string;
  /** Who or what, listed: "Ana Ruiz, Ben Ko and 2 more". No closing period. */
  detail?: string;
  /** How the step was settled, in sentences. */
  note?: string;
}

interface V3Shape {
  steps?: Partial<Record<StepId, StepConfig>>;
  participants?: Array<{ userId?: string | null; name?: string | null }>;
  items?: Array<{ id?: string | null }>;
  values?: Record<string, Record<string, unknown>>;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function toolName(toolId: string): string {
  return resolveSessionToolDescriptor(toolId)?.name ?? toolId.replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function namesLine(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/**
 * How a designer step was settled. Absent for sessions made outside the designer.
 * A step's saved run status is not updated when the session finishes (prod has
 * completed sessions whose S5 still reads 'running'), so once the session has a
 * result no step is called open.
 */
function howSettled(step: StepConfig | undefined, finished: boolean, fallbackTool?: string | null): string | undefined {
  if (!step) return undefined;
  if (step.mode === 'declared') return 'Set by the facilitator';
  const tool = step.toolId ?? fallbackTool;
  const base = tool ? `Decided together with ${toolName(tool)}` : 'Decided together';
  return step.runStatus === 'running' && !finished ? `${base}, still open` : base;
}

const joinNote = (...parts: (string | undefined | null | false)[]) => {
  const kept = parts.filter((p): p is string => !!p);
  return kept.length ? kept.join('. ') + '.' : undefined;
};

export function summarizeSessionSteps(session: SessionStepsSource, ctx: SessionStepsContext): SessionStepLine[] {
  const v3 = ((session.modeConfig as { v3Config?: V3Shape } | null | undefined)?.v3Config ?? {}) as V3Shape;
  const steps = v3.steps ?? {};
  const tool = session.selectedToolId || undefined;
  const methodTool = tool ? normalizeSessionToolId(tool) ?? tool : undefined;
  const label = (step: StepId) => `${step} · ${STEP_META[step].label}`;
  const finished = ctx.rank != null || !!session.runCompletedAt;
  const how = (step: StepId, fallbackTool?: string | null) => howSettled(steps[step], finished, fallbackTool);

  // ── S1 Participants ──
  const people = new Map<string, string | undefined>();
  for (const id of session.participantIds ?? []) if (id) people.set(id, ctx.nameOf?.(id) ?? undefined);
  for (const p of v3.participants ?? []) {
    const key = p.userId || `name:${p.name ?? ''}`;
    if (!p.userId && !p.name) continue;
    if (!people.get(key)) people.set(key, (p.userId ? ctx.nameOf?.(p.userId) : undefined) ?? p.name ?? undefined);
  }
  const names = [...people.values()].filter((n): n is string => !!n);
  const s1: SessionStepLine = {
    step: 'S1',
    label: label('S1'),
    state: people.size > 0 ? 'done' : 'open',
    value: people.size > 0 ? plural(people.size, 'person', 'people') : 'No one recorded',
    detail: namesLine(names) || undefined,
    note: joinNote(how('S1')),
  };

  // ── S2 Items ──
  const itemIds = new Set<string>();
  for (const id of session.itemIds ?? []) if (id) itemIds.add(id);
  for (const i of v3.items ?? []) if (i.id) itemIds.add(i.id);
  const ranked = session.rankingOutput?.rankedItemIds ?? [];
  const itemCount = Math.max(itemIds.size + (session.isolatedItems?.length ?? 0), ranked.length, ctx.outOf ?? 0);
  const s2: SessionStepLine = {
    step: 'S2',
    label: label('S2'),
    state: itemCount > 0 ? 'done' : 'open',
    value: itemCount > 0 ? plural(itemCount, 'item') : 'None recorded',
    note: joinNote(how('S2')),
  };

  // ── S3 Criteria ──
  const resolved = resolveSessionScoringCriteria(session, [], ctx.attributeFor);
  const criteria = resolved.source === 'tool_default' ? [] : resolved.criteria;
  const equal = criteria.length > 1 && criteria.every((c) => c.weight === criteria[0].weight);
  const methodScores = methodTool ? toolAsksForCriteria(methodTool) : false;
  const s3: SessionStepLine = {
    step: 'S3',
    label: label('S3'),
    state: criteria.length > 0 ? 'done' : 'open',
    value:
      criteria.length === 0
        ? 'None'
        : equal || criteria.length === 1
          ? `${criteria.map((c) => c.name).join(', ')}${equal ? ' · equal weight' : ''}`
          : criteria.map((c) => `${c.name} ${c.weight}%`).join(' · '),
    note: joinNote(
      how('S3'),
      tool && !methodScores && `${toolName(tool)} ranks the items without scoring them on criteria`,
    ),
  };

  // ── S4 Values ──
  const values = v3.values ?? {};
  const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  let filled = 0;
  for (const row of Object.values(values)) for (const c of criteria) if (isNum(row?.[c.id])) filled += 1;
  const total = itemCount * criteria.length;
  const mine = criteria.filter((c) => isNum(values[ctx.itemId]?.[c.id])).map((c) => `${c.name} ${values[ctx.itemId][c.id]}`);
  // Where each criterion's values come from (1062), when the track said: the
  // reader learns "Cost comes from the record · Impact is scored by the room"
  // instead of only "Set by the facilitator".
  const engineCriteria = (v3 as { criteria?: Array<{ id?: string; valuesFrom?: string | null; valuesFromPersonId?: string | null }> }).criteria ?? [];
  const fromOf = (id: string) => engineCriteria.find((c) => c.id === id);
  const fromLine = criteria.some((c) => isValuesFrom(fromOf(c.id)?.valuesFrom))
    ? valuesFromSentence(criteria.map((c) => {
        const e = fromOf(c.id);
        const from = isValuesFrom(e?.valuesFrom) ? e!.valuesFrom : null;
        return { name: c.name, from, personName: e?.valuesFromPersonId ? ctx.nameOf?.(e.valuesFromPersonId) ?? 'one person' : null };
      }))
    : undefined;
  let s4: SessionStepLine;
  if (filled > 0) {
    s4 = {
      step: 'S4',
      label: label('S4'),
      state: total > 0 && filled >= total ? 'done' : 'partial',
      value: mine.length ? mine.join(' · ') : 'None for this item',
      note: joinNote(`${filled} of ${total} values entered`, fromLine, how('S4')),
    };
  } else if (criteria.length > 0 && methodScores && tool) {
    s4 = {
      step: 'S4',
      label: label('S4'),
      state: ctx.rank != null ? 'done' : 'open',
      value: `Scored in ${toolName(tool)}`,
      note: joinNote("Each person's scores are kept with their responses, not on the session", fromLine, how('S4')),
    };
  } else {
    s4 = {
      step: 'S4',
      label: label('S4'),
      state: 'open',
      value: 'Not used',
      note: joinNote(
        criteria.length === 0 ? 'No criteria, so nothing to value' : tool ? `${toolName(tool)} ranks without values` : undefined,
        how('S4'),
      ),
    };
  }

  // ── S5 Ranking ──
  const s5: SessionStepLine = {
    step: 'S5',
    label: label('S5'),
    state: ctx.rank != null ? 'done' : 'open',
    value: ctx.rank != null ? `This item: ${ctx.rank}${ctx.outOf ? ` of ${ctx.outOf}` : ''}` : 'Not ranked yet',
    note: joinNote(how('S5', tool) ?? (tool ? `Ranked with ${toolName(tool)}` : undefined)),
  };

  const byStep: Record<StepId, SessionStepLine> = { S1: s1, S2: s2, S3: s3, S4: s4, S5: s5 };
  return STEP_ORDER.map((s) => byStep[s]);
}
